import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import routes from '../../utils/routes';
import { FiHome, FiTrendingUp, FiPieChart, FiUser, FiDollarSign, FiMenu } from 'react-icons/fi';
import SideNav from './SideNav';

const MainLayout: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const tabs = [
    { name: 'Home', icon: FiHome, route: routes.home },
    { name: 'Market', icon: FiTrendingUp, route: routes.market },
    { name: 'Portfolio', icon: FiPieChart, route: routes.portfolio },
    { name: 'Wallet', icon: FiDollarSign, route: routes.wallet },
    { name: 'Profile', icon: FiUser, route: routes.profile },
  ];

  const isActiveTab = (route: string) => {
    return location.pathname === route || location.pathname.startsWith(route + '/');
  };

  return (
    <div
      className="flex min-h-screen"
      style={{ backgroundColor: colors.backgroundColor, color: colors.textColor }}
    >
      {/* Mobile Header with hamburger menu */}
      <header
        className="lg:hidden fixed top-0 left-0 right-0 h-14 flex items-center px-4 z-40 border-b"
        style={{
          backgroundColor: colors.backgroundColor,
          borderColor: colors.borderColor,
        }}
      >
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="p-2 rounded-lg hover:opacity-80 transition-opacity"
          style={{ backgroundColor: colors.inputBackground }}
        >
          {React.createElement(FiMenu as any, { size: 22, color: colors.textColor })}
        </button>
        <div className="flex items-center gap-2 ml-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)` }}
          >
            <span className="text-white font-bold text-sm">F</span>
          </div>
          <span className="font-semibold" style={{ color: colors.primary }}>
            FinYantra
          </span>
        </div>
      </header>

      {/* Mobile Sidebar Drawer */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-50 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Mobile SideNav */}
          <SideNav isMobile onClose={() => setMobileMenuOpen(false)} />
        </>
      )}

      {/* Side Navigation - Desktop only (lg+) */}
      <SideNav />

      {/* Main Content - add top padding for mobile header */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 overflow-y-auto min-h-screen">
        <div className="w-full h-full">
          <Outlet />
        </div>
      </main>

      {/* Bottom Navigation - Mobile/Tablet only (hidden on lg+) */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 h-[70px] flex justify-around items-center z-50 border-t shadow-[0_-2px_10px_rgba(0,0,0,0.1)]"
        style={{
          backgroundColor: colors.backgroundColor,
          borderColor: colors.borderColor,
        }}
      >
        {tabs.map((tab) => {
          const isActive = isActiveTab(tab.route);
          const Icon = tab.icon;
          return (
            <button
              key={tab.name}
              onClick={() => navigate(tab.route)}
              className="flex flex-col items-center justify-center gap-1 py-2 px-4 bg-transparent border-none cursor-pointer transition-colors duration-300"
              style={{ color: isActive ? colors.primary : colors.grayScale500 }}
            >
              {React.createElement(Icon as any, { size: 24 })}
              <span className="text-xs font-medium">{tab.name}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default MainLayout;
